import React, { useState, useRef, useCallback, useEffect } from 'react';
import { storage } from '../../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// ── Helpers ───────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9);

const DEFAULT_BG = '#f4f6fb';
const DEFAULT_BODY_BG = '#ffffff';
const FONT = "'Arial', 'Helvetica Neue', sans-serif";

const BLOCK_TYPES = [
  { type: 'header',  icon: '🏷️',  label: 'Header' },
  { type: 'text',    icon: '📝',  label: 'Text' },
  { type: 'image',   icon: '🖼️',  label: 'Image' },
  { type: 'button',  icon: '🔘',  label: 'Button' },
  { type: 'columns', icon: '⬛',  label: '2 Columns' },
  { type: 'divider', icon: '➖',  label: 'Divider' },
  { type: 'spacer',  icon: '↕️',  label: 'Spacer' },
  { type: 'footer',  icon: '📋',  label: 'Footer' },
];

const TAGS = ['{{name}}','{{email}}','{{phone}}','{{course}}','{{intake}}','{{university}}'];

const createBlock = (type) => {
  switch (type) {
    case 'header':  return { id: uid(), type: 'header',  logo: '', title: 'Your Company', subtitle: 'Welcome to our newsletter', bgColor: '#7c3aed', textColor: '#ffffff', align: 'center' };
    case 'text':    return { id: uid(), type: 'text',    html: '<p style="margin:0;font-size:15px;line-height:1.7;color:#374151;">Type your message here. Use the toolbar to format text.</p>', padding: '20px 30px' };
    case 'image':   return { id: uid(), type: 'image',   src: '', alt: 'Image', align: 'center', link: '', width: 100, padding: '16px 30px' };
    case 'button':  return { id: uid(), type: 'button',  text: 'Click Here →', url: 'https://', bgColor: '#7c3aed', textColor: '#ffffff', align: 'center', radius: 8, size: 'medium' };
    case 'columns': return { id: uid(), type: 'columns', col1: { html: '<p style="margin:0;font-size:14px;color:#374151;">Column 1 content</p>', bgColor: '' }, col2: { html: '<p style="margin:0;font-size:14px;color:#374151;">Column 2 content</p>', bgColor: '' } };
    case 'divider': return { id: uid(), type: 'divider', color: '#e5e7eb', height: 1, padding: '8px 30px' };
    case 'spacer':  return { id: uid(), type: 'spacer',  height: 32 };
    case 'footer':  return { id: uid(), type: 'footer',  text: '© 2025 Your Company. All rights reserved.\nYou received this because you enquired about studying abroad.', bgColor: '#f8fafc', textColor: '#94a3b8', align: 'center' };
    default:        return { id: uid(), type: 'text',    html: '', padding: '16px 30px' };
  }
};

const STARTER_BLOCKS = ['header', 'text', 'button', 'footer'];

// ── HTML generation ───────────────────────────────────────────────────────────
function blockToHtmlRow(block) {
  switch (block.type) {
    case 'header': return `
      <tr><td style="background:${block.bgColor};padding:32px 30px;text-align:${block.align};">
        ${block.logo ? `<img src="${block.logo}" alt="logo" style="max-height:50px;margin-bottom:14px;display:block;${block.align === 'center' ? 'margin-left:auto;margin-right:auto;' : ''}" />` : ''}
        <h1 style="margin:0;font-family:${FONT};font-size:26px;font-weight:800;color:${block.textColor};letter-spacing:-0.5px;">${block.title}</h1>
        ${block.subtitle ? `<p style="margin:8px 0 0;font-family:${FONT};font-size:14px;color:${block.textColor};opacity:0.85;">${block.subtitle}</p>` : ''}
      </td></tr>`;
    case 'text': return `
      <tr><td style="padding:${block.padding || '20px 30px'};font-family:${FONT};">
        ${block.html || ''}
      </td></tr>`;
    case 'image': return `
      <tr><td style="padding:${block.padding || '16px 30px'};text-align:${block.align};">
        ${block.src
          ? `${block.link ? `<a href="${block.link}" style="display:inline-block;">` : ''}
             <img src="${block.src}" alt="${block.alt || ''}" style="max-width:${block.width || 100}%;height:auto;display:block;${block.align === 'center' ? 'margin:auto;' : block.align === 'right' ? 'margin-left:auto;' : ''}" />
             ${block.link ? '</a>' : ''}`
          : `<div style="background:#f1f5f9;border:2px dashed #cbd5e1;border-radius:8px;padding:40px;text-align:center;color:#94a3b8;font-size:13px;font-family:${FONT};">Image placeholder</div>`}
      </td></tr>`;
    case 'button': {
      const bSize = block.size === 'large' ? '16px 40px' : block.size === 'small' ? '8px 20px' : '12px 32px';
      const bFont = block.size === 'large' ? '16px' : block.size === 'small' ? '12px' : '14px';
      return `
      <tr><td style="padding:16px 30px;text-align:${block.align};">
        <a href="${block.url || '#'}" style="display:inline-block;background:${block.bgColor};color:${block.textColor};text-decoration:none;padding:${bSize};border-radius:${block.radius || 8}px;font-family:${FONT};font-size:${bFont};font-weight:700;letter-spacing:0.3px;">${block.text}</a>
      </td></tr>`;
    }
    case 'columns': return `
      <tr><td style="padding:16px 10px;">
        <table width="100%" cellpadding="0" cellspacing="0"><tr>
          <td width="50%" style="padding:0 10px;vertical-align:top;${block.col1.bgColor ? `background:${block.col1.bgColor};` : ''}font-family:${FONT};">${block.col1.html || ''}</td>
          <td width="50%" style="padding:0 10px;vertical-align:top;${block.col2.bgColor ? `background:${block.col2.bgColor};` : ''}font-family:${FONT};">${block.col2.html || ''}</td>
        </tr></table>
      </td></tr>`;
    case 'divider': return `
      <tr><td style="padding:${block.padding || '8px 30px'};">
        <div style="height:${block.height || 1}px;background:${block.color || '#e5e7eb'};"></div>
      </td></tr>`;
    case 'spacer': return `
      <tr><td style="height:${block.height || 32}px;font-size:0;line-height:0;">&nbsp;</td></tr>`;
    case 'footer': return `
      <tr><td style="background:${block.bgColor || '#f8fafc'};padding:24px 30px;text-align:${block.align || 'center'};">
        <p style="margin:0;font-family:${FONT};font-size:12px;color:${block.textColor || '#94a3b8'};line-height:1.6;">${(block.text || '').replace(/\n/g, '<br/>')}</p>
      </td></tr>`;
    default: return '';
  }
}

export function blocksToFullHtml(blocks, subject) {
  const rows = (blocks || []).map(blockToHtmlRow).join('\n');
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>${subject || 'Email'}</title>
</head>
<body style="margin:0;padding:0;background:${DEFAULT_BG};font-family:${FONT};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${DEFAULT_BG};padding:24px 0;">
<tr><td align="center">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:${DEFAULT_BODY_BG};border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
${rows}
</table>
</td></tr>
</table>
</body>
</html>`;
}

// ── ImageUpload ───────────────────────────────────────────────────────────────
function ImgUpload({ value, onChange, small }) {
  const [prog, setProg] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const handleFile = e => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    const path = 'email-images/' + Date.now() + '_' + file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const task = uploadBytesResumable(ref(storage, path), file);
    task.on('state_changed',
      snap => setProg(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      () => setUploading(false),
      () => { getDownloadURL(task.snapshot.ref).then(url => { onChange(url); setUploading(false); }); }
    );
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {value && <img src={value} alt="" style={{ height: small ? 28 : 40, maxWidth: 80, objectFit: 'contain', borderRadius: 5, border: '1px solid rgba(0,0,0,0.08)' }} />}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ padding: small ? '4px 10px' : '6px 12px', borderRadius: 7, border: '1.5px dashed rgba(124,58,237,0.35)', background: 'rgba(124,58,237,0.04)', color: '#7c3aed', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
          {uploading ? prog + '%…' : value ? '🔄 Replace' : '📁 Upload'}
        </button>
        {value && <button type="button" onClick={() => onChange('')} style={{ padding: '4px 8px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.04)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>✕</button>}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
      </div>
      {uploading && <div style={{ marginTop: 4, height: 3, background: 'rgba(0,0,0,0.06)', borderRadius: 2 }}><div style={{ height: '100%', width: prog + '%', background: '#7c3aed', transition: 'width 0.2s', borderRadius: 2 }} /></div>}
    </div>
  );
}

// ── Property Panel Helpers ────────────────────────────────────────────────────
const Row = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>}
    {children}
  </div>
);

const PInput = ({ value, onChange, type = 'text', placeholder, style }) => (
  <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.12)', fontSize: 12, color: '#0f172a', outline: 'none', boxSizing: 'border-box', ...style }} />
);

const ColorInput = ({ value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
      style={{ width: 32, height: 32, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 6, background: 'transparent' }} />
    <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#000000"
      style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.1)', fontSize: 12, color: '#0f172a', outline: 'none' }} />
  </div>
);

const AlignPicker = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {['left', 'center', 'right'].map(a => (
      <button key={a} onClick={() => onChange(a)}
        style={{ flex: 1, padding: '6px', borderRadius: 7, border: '1px solid ' + (value === a ? '#7c3aed' : 'rgba(0,0,0,0.1)'), background: value === a ? 'rgba(124,58,237,0.1)' : 'transparent', color: value === a ? '#7c3aed' : '#64748b', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
        {a === 'left' ? '⬅' : a === 'center' ? '↔' : '➡'}
      </button>
    ))}
  </div>
);

// ── Block Property Panels ─────────────────────────────────────────────────────
function HeaderPanel({ block, update }) {
  return (
    <div>
      <Row label="Logo"><ImgUpload value={block.logo} onChange={v => update('logo', v)} small /></Row>
      <Row label="Title"><PInput value={block.title} onChange={v => update('title', v)} placeholder="Company Name" /></Row>
      <Row label="Subtitle"><PInput value={block.subtitle} onChange={v => update('subtitle', v)} placeholder="Optional tagline" /></Row>
      <Row label="Background"><ColorInput value={block.bgColor} onChange={v => update('bgColor', v)} /></Row>
      <Row label="Text Color"><ColorInput value={block.textColor} onChange={v => update('textColor', v)} /></Row>
      <Row label="Alignment"><AlignPicker value={block.align} onChange={v => update('align', v)} /></Row>
    </div>
  );
}

function TextPanel({ block, update, insertTag, editingRef }) {
  return (
    <div>
      <Row label="Format">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
          {[['B', 'bold', 700], ['I', 'italic', 'normal', 'italic'], ['U', 'underline', 'normal', 'normal', 'underline']].map(([l, cmd, fw, fs, td]) => (
            <button key={cmd} onMouseDown={e => { e.preventDefault(); document.execCommand(cmd); editingRef.current?.focus(); }}
              style={{ padding: '4px 9px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 12, cursor: 'pointer', fontWeight: fw || 400, fontStyle: fs || 'normal', textDecoration: td || 'none' }}>{l}</button>
          ))}
          {[['H1', 'formatBlock', 'h1'], ['H2', 'formatBlock', 'h2'], ['P', 'formatBlock', 'p']].map(([l, cmd, arg]) => (
            <button key={l} onMouseDown={e => { e.preventDefault(); document.execCommand(cmd, false, arg); editingRef.current?.focus(); }}
              style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['⬅', 'justifyLeft'], ['↔', 'justifyCenter'], ['➡', 'justifyRight']].map(([l, cmd]) => (
            <button key={cmd} onMouseDown={e => { e.preventDefault(); document.execCommand(cmd); editingRef.current?.focus(); }}
              style={{ flex: 1, padding: '5px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 11, cursor: 'pointer' }}>{l}</button>
          ))}
          <button onMouseDown={e => { e.preventDefault(); const url = window.prompt('Link URL:'); if (url) document.execCommand('createLink', false, url); editingRef.current?.focus(); }}
            style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 11, cursor: 'pointer' }}>🔗</button>
        </div>
      </Row>
      <Row label="Personalisation">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {TAGS.map(tag => (
            <button key={tag} onClick={() => insertTag(block.id, tag)}
              style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid rgba(124,58,237,0.25)', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>
              {tag}
            </button>
          ))}
        </div>
      </Row>
      <Row label="Padding"><PInput value={block.padding} onChange={v => update('padding', v)} placeholder="20px 30px" /></Row>
    </div>
  );
}

function ImagePanel({ block, update }) {
  return (
    <div>
      <Row label="Upload Image"><ImgUpload value={block.src} onChange={v => update('src', v)} /></Row>
      <Row label="or Image URL"><PInput value={block.src} onChange={v => update('src', v)} placeholder="https://..." /></Row>
      <Row label="Alt Text"><PInput value={block.alt} onChange={v => update('alt', v)} placeholder="Describe image" /></Row>
      <Row label="Link URL"><PInput value={block.link} onChange={v => update('link', v)} placeholder="https://..." /></Row>
      <Row label={`Width: ${block.width || 100}%`}>
        <input type="range" min={20} max={100} value={block.width || 100} onChange={e => update('width', Number(e.target.value))} style={{ width: '100%' }} />
      </Row>
      <Row label="Alignment"><AlignPicker value={block.align} onChange={v => update('align', v)} /></Row>
    </div>
  );
}

function ButtonPanel({ block, update }) {
  return (
    <div>
      <Row label="Button Text"><PInput value={block.text} onChange={v => update('text', v)} placeholder="Click Here" /></Row>
      <Row label="Link URL"><PInput value={block.url} onChange={v => update('url', v)} placeholder="https://..." /></Row>
      <Row label="Background"><ColorInput value={block.bgColor} onChange={v => update('bgColor', v)} /></Row>
      <Row label="Text Color"><ColorInput value={block.textColor} onChange={v => update('textColor', v)} /></Row>
      <Row label="Size">
        <div style={{ display: 'flex', gap: 6 }}>
          {['small', 'medium', 'large'].map(s => (
            <button key={s} onClick={() => update('size', s)}
              style={{ flex: 1, padding: '6px', borderRadius: 7, border: '1px solid ' + (block.size === s ? '#7c3aed' : 'rgba(0,0,0,0.1)'), background: block.size === s ? 'rgba(124,58,237,0.1)' : 'transparent', color: block.size === s ? '#7c3aed' : '#64748b', fontSize: 11, cursor: 'pointer', fontWeight: 600, textTransform: 'capitalize' }}>{s}</button>
          ))}
        </div>
      </Row>
      <Row label="Alignment"><AlignPicker value={block.align} onChange={v => update('align', v)} /></Row>
      <Row label={`Radius: ${block.radius || 8}px`}>
        <input type="range" min={0} max={50} value={block.radius || 8} onChange={e => update('radius', Number(e.target.value))} style={{ width: '100%' }} />
      </Row>
    </div>
  );
}

function ColumnsPanel({ block, update }) {
  return (
    <div>
      <Row label="Col 1 Background"><ColorInput value={block.col1.bgColor} onChange={v => update('col1', { ...block.col1, bgColor: v })} /></Row>
      <Row label="Col 2 Background"><ColorInput value={block.col2.bgColor} onChange={v => update('col2', { ...block.col2, bgColor: v })} /></Row>
      <div style={{ fontSize: 11, color: '#94a3b8', padding: '8px', background: 'rgba(0,0,0,0.03)', borderRadius: 7 }}>
        💡 Click each column in the canvas to edit text inline
      </div>
    </div>
  );
}

function DividerPanel({ block, update }) {
  return (
    <div>
      <Row label="Color"><ColorInput value={block.color} onChange={v => update('color', v)} /></Row>
      <Row label={`Thickness: ${block.height || 1}px`}>
        <input type="range" min={1} max={8} value={block.height || 1} onChange={e => update('height', Number(e.target.value))} style={{ width: '100%' }} />
      </Row>
    </div>
  );
}

function SpacerPanel({ block, update }) {
  return (
    <div>
      <Row label={`Height: ${block.height || 32}px`}>
        <input type="range" min={8} max={120} value={block.height || 32} onChange={e => update('height', Number(e.target.value))} style={{ width: '100%' }} />
      </Row>
    </div>
  );
}

function FooterPanel({ block, update }) {
  return (
    <div>
      <Row label="Footer Text">
        <textarea value={block.text || ''} onChange={e => update('text', e.target.value)} rows={4}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.12)', fontSize: 12, resize: 'vertical', outline: 'none', lineHeight: 1.5, boxSizing: 'border-box' }} />
      </Row>
      <Row label="Background"><ColorInput value={block.bgColor} onChange={v => update('bgColor', v)} /></Row>
      <Row label="Text Color"><ColorInput value={block.textColor} onChange={v => update('textColor', v)} /></Row>
    </div>
  );
}

// ── Block Canvas Renderer ─────────────────────────────────────────────────────
function BlockPreview({ block, selected, onSelect, onEditableChange, editingRef }) {
  const editable = (html, onBlur) => (
    <div
      contentEditable
      suppressContentEditableWarning
      ref={selected ? editingRef : null}
      onBlur={e => onBlur(e.currentTarget.innerHTML)}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ outline: 'none', minHeight: 20 }}
      onClick={e => e.stopPropagation()}
    />
  );

  let inner;
  switch (block.type) {
    case 'header':
      inner = (
        <div style={{ background: block.bgColor, padding: '28px 30px', textAlign: block.align }}>
          {block.logo && <img src={block.logo} alt="logo" style={{ maxHeight: 48, marginBottom: 12, display: 'block', ...(block.align === 'center' ? { margin: '0 auto 12px' } : {}) }} />}
          <h1 style={{ margin: 0, fontFamily: FONT, fontSize: 24, fontWeight: 800, color: block.textColor }}>{block.title}</h1>
          {block.subtitle && <p style={{ margin: '6px 0 0', fontFamily: FONT, fontSize: 13, color: block.textColor, opacity: 0.85 }}>{block.subtitle}</p>}
        </div>
      ); break;
    case 'text':
      inner = (
        <div style={{ padding: block.padding || '20px 30px', fontFamily: FONT }}>
          {editable(block.html, html => onEditableChange(block.id, 'html', html))}
        </div>
      ); break;
    case 'image':
      inner = (
        <div style={{ padding: block.padding || '16px 30px', textAlign: block.align }}>
          {block.src
            ? <img src={block.src} alt={block.alt || ''} style={{ maxWidth: (block.width || 100) + '%', height: 'auto', borderRadius: 4 }} />
            : <div style={{ background: '#f1f5f9', border: '2px dashed #cbd5e1', borderRadius: 8, padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 12, fontFamily: FONT }}>📷 Upload or paste an image URL in the panel →</div>
          }
        </div>
      ); break;
    case 'button': {
      const bPad = block.size === 'large' ? '14px 36px' : block.size === 'small' ? '8px 18px' : '11px 28px';
      const bFs = block.size === 'large' ? '16px' : block.size === 'small' ? '12px' : '14px';
      inner = (
        <div style={{ padding: '14px 30px', textAlign: block.align, fontFamily: FONT }}>
          <span style={{ display: 'inline-block', background: block.bgColor, color: block.textColor, padding: bPad, borderRadius: (block.radius || 8) + 'px', fontSize: bFs, fontWeight: 700, letterSpacing: 0.3, userSelect: 'none', cursor: 'default' }}>{block.text}</span>
        </div>
      ); break;
    }
    case 'columns':
      inner = (
        <div style={{ display: 'flex', fontFamily: FONT }}>
          <div style={{ flex: 1, padding: '14px 16px', background: block.col1.bgColor || 'transparent', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
            {editable(block.col1.html, html => onEditableChange(block.id, 'col1', { ...block.col1, html }))}
          </div>
          <div style={{ flex: 1, padding: '14px 16px', background: block.col2.bgColor || 'transparent' }}>
            {editable(block.col2.html, html => onEditableChange(block.id, 'col2', { ...block.col2, html }))}
          </div>
        </div>
      ); break;
    case 'divider':
      inner = (
        <div style={{ padding: block.padding || '8px 30px' }}>
          <div style={{ height: (block.height || 1) + 'px', background: block.color || '#e5e7eb' }} />
        </div>
      ); break;
    case 'spacer':
      inner = (
        <div style={{ height: (block.height || 32) + 'px', background: 'rgba(124,58,237,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 10, color: '#c4c9d4', userSelect: 'none' }}>↕ {block.height || 32}px spacer</span>
        </div>
      ); break;
    case 'footer':
      inner = (
        <div style={{ background: block.bgColor || '#f8fafc', padding: '20px 30px', textAlign: block.align || 'center', fontFamily: FONT }}>
          <p style={{ margin: 0, fontSize: 11, color: block.textColor || '#94a3b8', lineHeight: 1.6 }}>
            {(block.text || '').split('\n').map((l, i, arr) => <span key={i}>{l}{i < arr.length - 1 && <br />}</span>)}
          </p>
        </div>
      ); break;
    default:
      inner = <div style={{ padding: 16, color: '#94a3b8', fontSize: 12 }}>Unknown block</div>;
  }

  return (
    <div
      style={{ position: 'relative', cursor: 'pointer', outline: selected ? '2px solid #7c3aed' : '2px solid transparent', outlineOffset: selected ? 2 : 0, borderRadius: 2, transition: 'outline 0.1s' }}
      onClick={() => onSelect(block.id)}
    >
      {inner}
      {selected && (
        <div style={{ position: 'absolute', top: 0, right: 0, background: '#7c3aed', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: '0 2px 0 6px', letterSpacing: 0.5, textTransform: 'uppercase', zIndex: 10 }}>
          {block.type}
        </div>
      )}
    </div>
  );
}

// ── Main EmailEditor ──────────────────────────────────────────────────────────
export default function EmailEditor({ value, onChange, compact }) {
  const [blocks, setBlocks] = useState(() => {
    if (value) {
      try {
        const m = value.match(/<!--clarion-block-data:(.*?)-->/s);
        if (m) return JSON.parse(decodeURIComponent(m[1]));
      } catch {}
    }
    return STARTER_BLOCKS.map(t => createBlock(t));
  });
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('visual');
  const [htmlCode, setHtmlCode] = useState('');
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const editingRef = useRef(null);

  const selectedBlock = blocks.find(b => b.id === selectedId);

  const syncHtml = useCallback((bs) => {
    const full = blocksToFullHtml(bs)
      + '\n<!--clarion-block-data:' + encodeURIComponent(JSON.stringify(bs)) + '-->';
    setHtmlCode(full);
    if (onChange) onChange(full);
  }, [onChange]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { syncHtml(blocks); }, []);

  const updateBlocks = useCallback((newBlocks) => {
    setBlocks(newBlocks);
    syncHtml(newBlocks);
  }, [syncHtml]);

  const addBlock = useCallback((type) => {
    const nb = createBlock(type);
    updateBlocks([...blocks, nb]);
    setSelectedId(nb.id);
    setShowBlockPicker(false);
  }, [blocks, updateBlocks]);

  const updateBlock = useCallback((id, key, val) => {
    updateBlocks(blocks.map(b => b.id === id ? { ...b, [key]: val } : b));
  }, [blocks, updateBlocks]);

  const editableChange = useCallback((id, key, val) => {
    setBlocks(prev => {
      const nb = prev.map(b => b.id === id ? { ...b, [key]: val } : b);
      syncHtml(nb);
      return nb;
    });
  }, [syncHtml]);

  const moveBlock = useCallback((id, dir) => {
    const idx = blocks.findIndex(b => b.id === id);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === blocks.length - 1)) return;
    const nb = [...blocks];
    [nb[idx], nb[idx + dir]] = [nb[idx + dir], nb[idx]];
    updateBlocks(nb);
  }, [blocks, updateBlocks]);

  const deleteBlock = useCallback((id) => {
    updateBlocks(blocks.filter(b => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [blocks, selectedId, updateBlocks]);

  const insertTag = useCallback((blockId, tag) => {
    const b = blocks.find(x => x.id === blockId);
    if (!b || b.type !== 'text') return;
    updateBlock(blockId, 'html', (b.html || '') + '<span>' + tag + '</span>');
  }, [blocks, updateBlock]);

  const switchMode = (m) => {
    if (m === 'html' || m === 'preview') setHtmlCode(blocksToFullHtml(blocks));
    setMode(m);
  };

  const applyHtmlCode = () => {
    try {
      const m = htmlCode.match(/<!--clarion-block-data:(.*?)-->/s);
      if (m) {
        const parsed = JSON.parse(decodeURIComponent(m[1]));
        setBlocks(parsed);
        if (onChange) onChange(htmlCode);
        return;
      }
    } catch {}
    // Wrap raw HTML as a text block
    const nb = [{ id: uid(), type: 'text', html: htmlCode, padding: '0' }];
    setBlocks(nb);
    if (onChange) onChange(htmlCode);
  };

  const getPropPanel = () => {
    if (!selectedBlock) return null;
    const upd = (k, v) => updateBlock(selectedId, k, v);
    switch (selectedBlock.type) {
      case 'header':  return <HeaderPanel  block={selectedBlock} update={upd} />;
      case 'text':    return <TextPanel    block={selectedBlock} update={upd} insertTag={insertTag} editingRef={editingRef} />;
      case 'image':   return <ImagePanel   block={selectedBlock} update={upd} />;
      case 'button':  return <ButtonPanel  block={selectedBlock} update={upd} />;
      case 'columns': return <ColumnsPanel block={selectedBlock} update={upd} />;
      case 'divider': return <DividerPanel block={selectedBlock} update={upd} />;
      case 'spacer':  return <SpacerPanel  block={selectedBlock} update={upd} />;
      case 'footer':  return <FooterPanel  block={selectedBlock} update={upd} />;
      default: return null;
    }
  };

  const ModeBtn = ({ m, label, icon }) => (
    <button onClick={() => switchMode(m)}
      style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, background: mode === m ? '#7c3aed' : 'transparent', color: mode === m ? '#fff' : '#64748b', transition: 'all 0.15s' }}>
      <span style={{ fontSize: 14 }}>{icon}</span>{label}
    </button>
  );

  const canvasHeight = compact ? 400 : 560;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 14, overflow: 'hidden', background: '#f8fafc', fontFamily: FONT }}>

      {/* ── Top Bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'rgba(0,0,0,0.04)', borderRadius: 10, padding: 3 }}>
          <ModeBtn m="visual"  label="Visual"   icon="✏️" />
          <ModeBtn m="html"    label="HTML"     icon="</>" />
          <ModeBtn m="preview" label="Preview"  icon="👁️" />
        </div>
        <div style={{ flex: 1 }} />
        {mode === 'visual' && (
          <button onClick={() => setShowBlockPicker(!showBlockPicker)}
            style={{ padding: '7px 16px', borderRadius: 8, border: '1.5px dashed rgba(124,58,237,0.4)', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            + Add Block
          </button>
        )}
        {mode === 'html' && (
          <button onClick={applyHtmlCode}
            style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            ✓ Apply HTML
          </button>
        )}
      </div>

      {/* ── Block Picker ── */}
      {showBlockPicker && mode === 'visual' && (
        <div style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)', padding: '12px 14px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Add a block</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {BLOCK_TYPES.map(bt => (
              <button key={bt.type} onClick={() => addBlock(bt.type)}
                style={{ padding: '8px 14px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.1s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.08)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'; e.currentTarget.style.color = '#7c3aed'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)'; e.currentTarget.style.color = '#374151'; }}>
                <span style={{ fontSize: 16 }}>{bt.icon}</span>{bt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Main Area ── */}
      <div style={{ display: 'flex', flex: 1, minHeight: canvasHeight }}>

        {/* ── Left Panel: Properties ── */}
        {mode === 'visual' && (
          <div style={{ width: 228, background: '#fff', borderRight: '1px solid rgba(0,0,0,0.07)', overflowY: 'auto', flexShrink: 0 }}>
            {selectedBlock ? (
              <div style={{ padding: '14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 12, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <span>{BLOCK_TYPES.find(b => b.type === selectedBlock.type)?.icon}</span>
                  <span style={{ textTransform: 'capitalize' }}>{selectedBlock.type} Settings</span>
                </div>
                {getPropPanel()}
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', gap: 6 }}>
                  <button onClick={() => moveBlock(selectedId, -1)} title="Move up"
                    style={{ flex: 1, padding: '7px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>↑</button>
                  <button onClick={() => moveBlock(selectedId, 1)} title="Move down"
                    style={{ flex: 1, padding: '7px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>↓</button>
                  <button onClick={() => deleteBlock(selectedId)} title="Delete block"
                    style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.05)', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑</button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '28px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>👆</div>
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>Click any block in the canvas to select it and edit its properties here</div>
                <div style={{ marginTop: 20, padding: '10px 12px', background: 'rgba(124,58,237,0.06)', borderRadius: 9, border: '1px dashed rgba(124,58,237,0.2)' }}>
                  <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>💡 Tips</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>• Text blocks are editable inline<br/>• Use + Add Block to add elements<br/>• Switch to HTML tab for raw code</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Canvas / Code / Preview ── */}
        <div
          style={{ flex: 1, overflowY: 'auto', padding: mode === 'visual' ? '20px' : 0, background: mode === 'visual' ? DEFAULT_BG : mode === 'html' ? '#1a1d2e' : '#f4f6fb' }}
          onClick={e => { if (mode === 'visual' && e.target === e.currentTarget) setSelectedId(null); }}
        >
          {mode === 'visual' && (
            <div style={{ maxWidth: 600, margin: '0 auto', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.14)' }}>
              {blocks.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
                  <div style={{ fontSize: 13 }}>Your email is empty.<br />Click <strong style={{ color: '#7c3aed' }}>+ Add Block</strong> to start building.</div>
                </div>
              ) : blocks.map(block => (
                <BlockPreview
                  key={block.id}
                  block={block}
                  selected={selectedId === block.id}
                  onSelect={setSelectedId}
                  onEditableChange={editableChange}
                  editingRef={editingRef}
                />
              ))}
            </div>
          )}

          {mode === 'html' && (
            <textarea
              value={htmlCode}
              onChange={e => setHtmlCode(e.target.value)}
              style={{ width: '100%', height: '100%', minHeight: canvasHeight, padding: '16px', background: '#1a1d2e', color: '#a6e3a1', fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: 12, lineHeight: 1.7, border: 'none', outline: 'none', resize: 'none', boxSizing: 'border-box', tabSize: 2 }}
              spellCheck={false}
            />
          )}

          {mode === 'preview' && (
            <div style={{ padding: '20px', background: '#f4f6fb', minHeight: canvasHeight }}>
              <div style={{ maxWidth: 640, margin: '0 auto', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
                <iframe
                  title="email-preview"
                  srcDoc={blocksToFullHtml(blocks, 'Email Preview')}
                  style={{ width: '100%', height: Math.max(canvasHeight, 500), border: 'none', display: 'block' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom Status Bar ── */}
      <div style={{ padding: '7px 14px', background: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)', fontSize: 11, color: '#94a3b8', display: 'flex', gap: 16, alignItems: 'center' }}>
        <span style={{ color: '#7c3aed', fontWeight: 600 }}>{blocks.length} block{blocks.length !== 1 ? 's' : ''}</span>
        <span>•</span>
        <span>✏️ Click block to edit</span>
        <span>•</span>
        <span>&lt;/&gt; Switch to HTML for raw code editing</span>
        <span>•</span>
        <span>👁️ Preview renders the final email</span>
      </div>
    </div>
  );
}
